/** Mission 8's POW camp: the tiger pits, the concrete pyramid that guards the controls, and the lever. */

import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';

/**
 * Tiger pit: a barred grate flush with the ground in a low concrete rim. The black floor
 * under the bars is what makes it a hole rather than a slab. The rim stays bare concrete in
 * every biome - a white rim round a black square would vanish into arctic snow.
 */
export const tigerPit: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const rim = { all: p.color('concrete.mid'), top: p.color('concrete.hi'), front: p.color('concrete.lo') };
  mb.box(2.6, 0.3, 0.36, rim, place(0, 0, 1.12));
  mb.box(2.6, 0.3, 0.36, rim, place(0, 0, -1.12));
  mb.box(0.36, 0.3, 1.88, rim, place(1.12, 0, 0));
  mb.box(0.36, 0.3, 1.88, rim, place(-1.12, 0, 0));
  mb.box(1.9, 0.08, 1.9, p.color('ink.0'));
  // Bars 10 cm wide, 40 cm apart: about the finest grille that survives 18 px/m.
  for (const x of [-0.8, -0.4, 0, 0.4, 0.8]) mb.box(0.1, 0.08, 2.0, p.color('metal.mid'), place(x, 0.2, 0));
  for (const z of [-0.5, 0.5]) mb.box(2.0, 0.08, 0.12, p.color('metal.lo'), place(0, 0.17, z));
  return mb;
};

const PLINTH = { half: 0.9, h: 0.3 };
/** The point: a square pyramid this tall on the plinth; `r` is its base's corner radius. */
const POINT = { r: 1.12, h: 1.12 };

/** Half-width of the controls pyramid's solid at height `y` - what has to contain the lever. */
export function pyramidHalfWidthAt(y: number): number {
  if (y <= PLINTH.h) return PLINTH.half;
  return Math.max(0, (POINT.r / Math.SQRT2) * (1 - (y - PLINTH.h) / POINT.h));
}

/** Controls pyramid: blow it open to expose the lever. A plinth under the point gives the eave a shadow. */
export const controlsPyramid: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(PLINTH.half * 2, PLINTH.h, PLINTH.half * 2, { all: p.color('concrete.lo'), top: p.color('concrete.mid') });
  mb.cylinder(POINT.r, 0, POINT.h, 4, p.color('concrete.mid'), place(0, PLINTH.h, 0, Math.PI / 4));
  if (p.biome === 'arctic') mb.cylinder(0.5, 0, 0.52, 4, p.color('ground.3'), place(0, 0.93, 0, Math.PI / 4));
  // Inspection hatch with the enemy's mark, on the face the camera sees.
  mb.box(0.5, 0.2, 0.06, p.color('enemyAccent.0'), place(0, 0.05, 0.9));
  return mb;
};

/**
 * The lever that opens the pits. The knob is in the player's colour because it is his to pull
 * (05 §4.3). The generator stands it at the CENTRE of the controls pyramid, so until that is
 * blown the whole lever must fit inside the pyramid - hence the short arm and the slight lean.
 */
export const lever: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.45, 0.2, 0.45, { all: p.color('concrete.lo'), top: p.color('concrete.mid') });
  mb.box(0.3, 0.35, 0.22, p.color('metal.mid'), place(0, 0.2, 0));
  mb.with(place(0, 0.4, 0, 0, 1, 0, -0.15), () => {
    mb.box(0.08, 0.6, 0.08, p.color('metal.lo'));
    mb.box(0.2, 0.2, 0.2, p.color('playerAccent.0'), place(0, 0.5, 0));
  });
  return mb;
};
